/**
 * Which language the app speaks.
 *
 * It appears twice, and both places are deliberate. On the sign-in screen,
 * because a person who has installed a Polish-first app and does not read Polish
 * must be able to change that BEFORE the one screen with a single button in a
 * language they cannot read; and in settings, because that is where they will
 * look for it afterwards.
 *
 * Signed in, the choice is written to the user profile as well as to this
 * device, so the tablet in the whelping room opens in the language chosen on the
 * phone. Signed out there is no profile to write to and no session to write it
 * with, so it is a local preference until there is — `writeProfile` in
 * TenantProvider carries the current language into the document the first time
 * this person is seen.
 *
 * Two radios rather than a `<select>`: with two options a select costs a tap, a
 * sheet, a scroll and a second tap to show what a pair of buttons shows at rest,
 * and one of those buttons has to be readable by someone who is lost.
 */
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthProvider.tsx';
import { setProfileLocale } from '@/api/mutations.ts';
import { currentLocale, type Locale, LOCALES, setLocale } from '@/i18n/index.ts';
import { cn } from '@/lib/cn.ts';

export const LanguagePicker = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const active = currentLocale();

  const choose = (locale: Locale): void => {
    if (locale === active) return;
    setLocale(locale);
    // Not awaited, and nothing to wait for — the language has already changed.
    // See api/mutations.ts.
    if (user !== null) setProfileLocale(user.uid, locale);
  };

  return (
    <fieldset className={cn('flex flex-col gap-2', className)}>
      <legend className="mb-2 text-sm font-medium text-ink-soft">{t('settings.language')}</legend>

      <div className="flex gap-2">
        {LOCALES.map((locale) => (
          <label
            key={locale}
            // The input is visually hidden and the label is the control, which is
            // why the focus ring is put back by hand: `:focus-visible` on an
            // element clipped to a pixel is a ring nobody can see.
            className={cn(
              'flex min-h-tap flex-1 cursor-pointer items-center justify-center rounded-xl',
              'border border-line px-3 text-sm font-medium text-ink-soft transition-colors',
              'not-has-checked:hover:border-line-strong not-has-checked:hover:bg-sunken',
              'has-checked:border-brand-600 has-checked:bg-brand-50 has-checked:text-brand-ink',
              'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-brand-600',
            )}
          >
            <input
              type="radio"
              name="locale"
              value={locale}
              checked={active === locale}
              onChange={() => choose(locale)}
              className="sr-only"
            />
            {t(`language.${locale}`)}
          </label>
        ))}
      </div>

      {user !== null && <p className="text-sm text-ink-faint">{t('settings.languageHint')}</p>}
    </fieldset>
  );
};
